"use client";

import { useState, useRef, useEffect } from "react";
import { DayPicker, type DateRange, type Matcher } from "react-day-picker";
import { formatDateRange, formatDate } from "@/lib/format";

type DisabledRange = { from: string | Date; to: string | Date };

type Props = {
  nameStart?: string;
  nameEnd?: string;
  /** Uncontrolled initial values (used when no `onChange` is provided). */
  defaultStart?: string;
  defaultEnd?: string;
  /** Controlled values — if provided, the picker mirrors them and emits
      `onChange` whenever the user picks. */
  valueStart?: string;
  valueEnd?: string;
  onChange?: (start: string, end: string) => void;
  disabledRanges?: DisabledRange[];
  numberOfMonths?: 1 | 2;
  required?: boolean;
  /**
   * Render style. `default` = labelled white card button (used in forms);
   * `bare` = invisible trigger that just shows the chosen-range label, used
   * inside the homepage `.search-field` which already supplies its own
   * label + styling; `compact` = inline value-text used inside the search
   * page's `.compact-field` (no border, no eyebrow, opens a popover).
   */
  variant?: "default" | "bare" | "compact";
  /** Override the displayed text when no dates picked. */
  placeholder?: string;
};

export default function DateRangePicker({
  nameStart = "checkIn",
  nameEnd = "checkOut",
  defaultStart,
  defaultEnd,
  valueStart,
  valueEnd,
  onChange,
  disabledRanges = [],
  numberOfMonths = 2,
  required = true,
  variant = "default",
  placeholder,
}: Props) {
  const [open, setOpen] = useState(false);
  const isControlled = onChange != null;
  const [internalRange, setInternalRange] = useState<DateRange | undefined>(() => {
    const s = valueStart ?? defaultStart;
    const e = valueEnd ?? defaultEnd;
    if (!s || !e) {
      if (s) {
        const f = new Date(s);
        if (!isNaN(f.getTime())) return { from: f, to: undefined };
      }
      return undefined;
    }
    const f = new Date(s);
    const t = new Date(e);
    if (isNaN(f.getTime()) || isNaN(t.getTime())) return undefined;
    return { from: f, to: t };
  });

  // When controlled, derive the range from the parent's props.
  const range: DateRange | undefined = isControlled
    ? (() => {
        if (!valueStart && !valueEnd) return undefined;
        const f = valueStart ? new Date(valueStart) : undefined;
        const t = valueEnd ? new Date(valueEnd) : undefined;
        if (f && isNaN(f.getTime())) return undefined;
        if (t && isNaN(t.getTime())) return undefined;
        return { from: f, to: t };
      })()
    : internalRange;

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!open) return;
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const midSelection = Boolean(range?.from && !range?.to);
  const minSecondClick = midSelection && range?.from
    ? new Date(range.from.getTime() + 24 * 60 * 60 * 1000)
    : null;

  const disabled: Matcher[] = [
    { before: today },
    ...(minSecondClick ? [{ before: minSecondClick }] : []),
    ...disabledRanges.map((r) => ({
      from: typeof r.from === "string" ? new Date(r.from) : r.from,
      to: typeof r.to === "string" ? new Date(r.to) : r.to,
    })),
  ];

  const labelText = range?.from && range?.to
    ? formatDateRange(range.from, range.to)
    : range?.from
    ? `${formatDate(range.from)} → pick check-out`
    : placeholder ?? "Pick dates";

  function emit(next: DateRange | undefined) {
    if (isControlled) {
      onChange?.(
        next?.from ? formatDate(next.from) : "",
        next?.to ? formatDate(next.to) : "",
      );
    } else {
      setInternalRange(next);
    }
  }

  function handleSelect(r: DateRange | undefined) {
    if (!r) {
      emit(undefined);
      return;
    }
    // When a complete range is already in state and the user clicks a
    // new date, treat it as the start of a fresh selection rather than
    // letting DayPicker extend or shift the existing one.
    if (range?.from && range?.to && r.from && r.to) {
      const existingMs = [range.from.getTime(), range.to.getTime()];
      const clicked = [r.from, r.to].find((d) => !existingMs.includes(d.getTime()));
      if (clicked) {
        emit({ from: clicked, to: undefined });
        return;
      }
    }
    if (r.from && r.to && r.to.getTime() === r.from.getTime()) {
      emit({ from: r.from, to: undefined });
      return;
    }
    if (r.from && r.to && r.to < r.from) {
      emit({ from: r.to, to: r.from });
      setOpen(false);
      return;
    }
    emit(r);
    if (r.from && r.to) setOpen(false);
  }

  // On the compact variant we need the popover to break out of the narrow
  // .compact-field (~100px wide) and stretch to the viewport edges on
  // mobile, otherwise the calendar would be clipped to a single column.
  const calendarClass =
    variant === "compact"
      ? "drp-popover drp-popover-compact"
      : "drp-popover";
  const calendar = (
    <>
      {/* Backdrop — only visible on mobile via CSS so taps anywhere off
          the calendar dismiss it without needing a pixel-perfect tap. */}
      <button
        type="button"
        aria-label="Close calendar"
        onClick={() => setOpen(false)}
        className="drp-scrim"
      />
      <div className={calendarClass}>
        {/* Top-bar with title + close X, mobile-only via CSS. */}
        <div className="drp-popover-head">
          <span className="drp-popover-title">Select dates</span>
          <button
            type="button"
            aria-label="Close"
            onClick={() => setOpen(false)}
            className="drp-popover-close"
          >
            ×
          </button>
        </div>
        <DayPicker
          mode="range"
          selected={range}
          onSelect={handleSelect}
          disabled={disabled}
          numberOfMonths={
            typeof window !== "undefined" && window.matchMedia("(max-width: 720px)").matches
              ? 1
              : numberOfMonths
          }
          weekStartsOn={1}
        />
        {midSelection ? (
          <p className="text-xs text-neutral-500 mt-2 px-2">
            Now pick a check-out date.
          </p>
        ) : null}
        <div className="drp-popover-foot">
          {range?.from || range?.to ? (
            <button
              type="button"
              onClick={() => emit(undefined)}
              className="text-xs text-neutral-500 hover:underline"
            >
              Clear dates
            </button>
          ) : <span />}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="btn-pill rose sm"
          >
            Done
          </button>
        </div>
      </div>
    </>
  );

  const hiddenInputs = (
    <>
      <input
        type="hidden"
        name={nameStart}
        value={range?.from ? formatDate(range.from) : ""}
        required={required}
      />
      <input
        type="hidden"
        name={nameEnd}
        value={range?.to ? formatDate(range.to) : ""}
        required={required}
      />
    </>
  );

  // ── Variant: bare ───────────────────────────────────────────
  // Used by the homepage SearchBar where `.search-field` already supplies
  // an eyebrow label and the field background. We just render the text
  // value + popover so it visually nests inside the field.
  if (variant === "bare") {
    return (
      <div className="relative" ref={ref}>
        {hiddenInputs}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-left text-[15px] text-neutral-900 p-0 bg-transparent border-0 cursor-pointer outline-none w-full"
          style={{ color: range?.from ? "var(--ink)" : "var(--ink-4)" }}
        >
          {labelText}
        </button>
        {open ? calendar : null}
      </div>
    );
  }

  // ── Variant: compact ────────────────────────────────────────
  // Used inside the search page's sticky `.compact-field`. No hidden inputs
  // because the parent owns submission via onChange.
  if (variant === "compact") {
    return (
      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-left bg-transparent border-0 cursor-pointer p-0 outline-none"
          style={{ fontSize: 13.5, color: range?.from ? "var(--ink)" : "var(--ink)" }}
        >
          {labelText}
        </button>
        {open ? calendar : null}
      </div>
    );
  }

  // ── Variant: default ───────────────────────────────────────
  return (
    <div className="relative" ref={ref}>
      {hiddenInputs}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left border border-neutral-200 rounded-lg px-3 py-3 min-h-[48px] text-sm bg-white hover:border-neutral-400 focus:outline-none focus:border-rose-600"
      >
        <span className="block text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
          When
        </span>
        <span className={range?.from ? "text-neutral-900" : "text-neutral-400"}>{labelText}</span>
      </button>
      {open ? calendar : null}
    </div>
  );
}
